import { useTranslation } from 'react-i18next';
import { Search, RefreshCw, Filter as FilterIcon, X } from 'lucide-react';
import { CompanyBookingStatus, DriverGroupDto } from '@cargovibe/shared-types';
import { Input } from '@/shared/ui/input';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { DriverGroupFilter } from '@/modules/driver-groups';

interface Props {
  searchTerm: string;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  statusFilters: Set<CompanyBookingStatus>;
  onToggleStatus: (status: CompanyBookingStatus) => void;
  groups: DriverGroupDto[];
  activeGroupId: string | null;
  onSelectGroup: (groupId: string | null) => void;
  onClearFilters: () => void;
  isFetching: boolean;
  onRefresh: () => void;
}

export function BookingsFilterBar({
  searchTerm,
  onSearchChange,
  statusFilters,
  onToggleStatus,
  groups,
  activeGroupId,
  onSelectGroup,
  onClearFilters,
  isFetching,
  onRefresh,
}: Readonly<Props>): React.JSX.Element {
  const { t } = useTranslation();

  const preventDropdownSelect = (event: Event): void => {
    event.preventDefault();
  };

  const handleToggleStatus = (status: CompanyBookingStatus) => (): void => {
    onToggleStatus(status);
  };

  const activeFilterCount: number = statusFilters.size + (activeGroupId ? 1 : 0);

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative flex-1 min-w-50 max-w-md">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/60"
        />
        <Input
          placeholder={t('common.search')}
          value={searchTerm}
          onChange={onSearchChange}
          className="pl-9"
        />
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2">
            <FilterIcon size={14} />
            {t('common.filter')}
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>{t('bookings.filterByStatus')}</DropdownMenuLabel>
          {Object.values(CompanyBookingStatus).map((status) => (
            <DropdownMenuCheckboxItem
              key={status}
              checked={statusFilters.has(status)}
              onCheckedChange={handleToggleStatus(status)}
              onSelect={preventDropdownSelect}
            >
              {t(`bookings.status.${status}`)}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <DriverGroupFilter groups={groups} activeGroupId={activeGroupId} onSelect={onSelectGroup} />

      {activeFilterCount > 0 && (
        <Button variant="ghost" size="sm" onClick={onClearFilters} className="gap-1">
          <X size={14} />
          {t('common.reset')}
        </Button>
      )}

      <Button
        variant="outline"
        size="sm"
        onClick={onRefresh}
        disabled={isFetching}
        className="gap-2 ml-auto"
        title={t('common.refresh')}
      >
        <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
        {t('common.refresh')}
      </Button>
    </div>
  );
}
